import type { TDocumentDefinitions } from 'pdfmake/interfaces.js';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { browserPdfOutput, type PdfOutput, pdfErrorKey, renderPdf } from '../personnel/pdf.js';
import { errorCode } from '../personnel/source.js';
import type { ReportBrand } from '../voyage/source.js';
import type { RecordPdfT } from './record-pdf.js';

/** Kayıt PDF'inin çıktı yolu: marka ve PDF kaydetme/yazdırma (web ve masaüstü ayrı). */
export interface RecordPdfOutput {
  /** PDF başlığındaki şirket markası (ADR 0057 §7). */
  brand?(): Promise<ReportBrand | null>;
  /** Verilmezse tarayıcı yolu. */
  pdf?: PdfOutput;
}

/**
 * Kayıt PDF'ini yazdırma (ADR 0062 §3): kayıtlı içerikten üretilir; marka okunamazsa yalnız ad
 * boş kalır. Hata metni döner (ekran gösterir).
 */
export function useRecordPrint(output: RecordPdfOutput) {
  const { t } = useTranslation();
  const [printing, setPrinting] = useState(false);
  const print = async (
    build: (
      tr: RecordPdfT,
      brand: ReportBrand | null,
    ) => { definition: TDocumentDefinitions; fileName: string },
  ): Promise<string | null> => {
    setPrinting(true);
    try {
      const brand = (await output.brand?.().catch(() => null)) ?? null;
      const tr: RecordPdfT = (key, options) => String(t(key, options ?? {}));
      const { definition, fileName } = build(tr, brand);
      const target = output.pdf ?? browserPdfOutput();
      await target.print(fileName, () => renderPdf(definition, { print: true }));
      return null;
    } catch (error) {
      const key = pdfErrorKey(errorCode(error));
      return key === 'restHours.pdf.error' ? t('recordPdf.error') : t(key);
    } finally {
      setPrinting(false);
    }
  };
  return { printing, print };
}
